import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { LecturePage as LightLecturePage } from '@systemeio/light-lecture-viewer'
import { useTranslation } from 'next-i18next'
import { twJoin } from 'tailwind-merge'
import { AccessDenied, getAccessDeniedText } from 'shared/errors/access-denied'
import { useCompleteCourseLecture } from '../hooks/use-complete-course-lecture'
import { useCourseData } from '../hooks/use-course-data'
import { useCourseMenu } from '../hooks/use-course-menu'
import { useLightViewLecture } from '../hooks/use-light-view-lecture'
import useStepRouter from '../hooks/use-step-router'
import { CourseAccessTypeEnum } from '../types/course-interface'
import {
  CourseLectureAccessDeniedDataInterface,
  CourseLectureId,
} from '../types/course-lecture-interface'
import { addPublicCustomClassNamePrefix } from '../utils/add-class-prefix'
import { CourseContentSkeleton } from './course-content-skeleton'
import { CourseInfoScreenLayout } from './course-info-screens/course-info-screen-layout'
import { CoursePurchaseModuleScreen } from './course-info-screens/course-purchase-module-screen'
import { CourseStepButton } from './course-state-button'
import CourseTracker from './course-tracker'
import { SecondaryButtonWithCustomTheme } from './secondary-button-with-custom-theme'

interface LightCourseLectureProps {
  lectureId: CourseLectureId
}

export const LightCourseLecture = ({ lectureId }: LightCourseLectureProps) => {
  const { t } = useTranslation()

  const { step, onNextStep } = useStepRouter()
  const [moduleLink, setModuleLink] = useState<string | null>(null)
  const [shouldSendLog, setShouldSendLog] = useState(true)
  const [accessError, setAccessError] = useState<string | null>(null)

  const completeCourseLecture = useCompleteCourseLecture()
  const getCourseDataQuery = useCourseData()
  const getCourseMenuQuery = useCourseMenu({ courseId: getCourseDataQuery.courseData?.id })
  const getlightLectureQuery = useLightViewLecture({
    lectureId: lectureId.toString(),
    config: {
      isPaused: () => getCourseDataQuery.courseData?.courseType !== 'light_course',
      onError: error => {
        if (
          error instanceof AccessDenied &&
          typeof error.response?.data === 'object' &&
          error.response?.data !== null &&
          'reason' in error.response?.data
        ) {
          setAccessError(getAccessDeniedText(error.response?.data.reason))
        } else if (error instanceof AccessDenied) {
          const data = error.response?.data as
            | CourseLectureAccessDeniedDataInterface
            | null
            | undefined
          if (data && 'module' in data) {
            // NOTE: sales page for module
            setModuleLink(data.module as string)
          }
        } else {
          toast.error(t('global.error'))
        }
        setShouldSendLog(false)
      },
    },
  })

  const onCompleteCourseLecture = async () => {
    if (step?.id) {
      await completeCourseLecture.completeCourseLecture(step.id)
      await getCourseMenuQuery.mutate(
        data =>
          data?.map(module => ({
            ...module,
            lectures: module.lectures.map(lecture =>
              lecture.id === step?.id ? { ...lecture, completed: true } : lecture,
            ),
          })),
        false,
      )
      getCourseDataQuery.mutate()
      onNextStep()
    }
  }

  const loggingEnabled =
    !!getCourseDataQuery.courseData?.lectureSessionLogging &&
    !!getlightLectureQuery.data &&
    shouldSendLog

  const displayPurchaseScreen =
    !getlightLectureQuery.isValidating &&
    !!step &&
    ((!step.available &&
      getCourseDataQuery.courseData?.dominantAccessType === CourseAccessTypeEnum.PartialAccess) ||
      moduleLink)
  if (displayPurchaseScreen) return <CoursePurchaseModuleScreen moduleLink={moduleLink} />

  const isLoading = (getlightLectureQuery.isLoading || !getlightLectureQuery.data) && !accessError
  if (isLoading && !getlightLectureQuery.data) return <CourseContentSkeleton />

  if (accessError)
    return (
      <CourseInfoScreenLayout
        text={<span className="whitespace-pre-line">{t(accessError)}</span>}
      />
    )

  return (
    <div
      className={twJoin(
        'relative w-full',
        addPublicCustomClassNamePrefix('course-lecture__container'),
      )}
    >
      {loggingEnabled && <CourseTracker />}

      <div className="relative w-full overflow-y-auto rounded-md bg-THEME-block-background p-4 [&>*::-webkit-scrollbar]:hidden">
        {getlightLectureQuery.data && (
          <LightLecturePage
            name={getlightLectureQuery.data.name}
            body={getlightLectureQuery.data.body}
            video={getlightLectureQuery.data.video}
            attachments={getlightLectureQuery.data.attachments}
          />
        )}
      </div>

      <div className="mt-5 flex justify-center rounded-md bg-THEME-block-background p-6">
        {step?.completed ? (
          <CourseStepButton />
        ) : (
          <SecondaryButtonWithCustomTheme
            isLoading={completeCourseLecture.isFetching}
            className={twJoin(
              'font-bold',
              addPublicCustomClassNamePrefix('course__button--mark-as-completed'),
            )}
            onClick={onCompleteCourseLecture}
          >
            {t('course.mark_step_as_completed_button_label')}
          </SecondaryButtonWithCustomTheme>
        )}
      </div>
    </div>
  )
}
